import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, FileDown, ArrowRight, Package, Filter, X, ChevronLeft } from "lucide-react";

const STATUS_LABEL = {
  placed: "Placed", confirmed: "Confirmed", packed: "Packed",
  shipped: "Shipped", out_for_delivery: "Out for delivery",
  delivered: "Delivered", cancelled: "Cancelled",
};
const STATUS_OPTS = ["all", ...Object.keys(STATUS_LABEL)];
const RANGE_OPTS = [
  { v: "all", l: "All time" },
  { v: "30", l: "Last 30 days" },
  { v: "90", l: "Last 3 months" },
  { v: "180", l: "Last 6 months" },
  { v: "365", l: "Last year" },
];
const SORT_OPTS = [
  { v: "newest", l: "Newest first" },
  { v: "oldest", l: "Oldest first" },
  { v: "amount_desc", l: "Amount: high to low" },
  { v: "amount_asc", l: "Amount: low to high" },
];

const invoiceHref = (orderNumber) =>
  `${API}/orders/${encodeURIComponent(orderNumber)}/invoice.pdf`;

export default function MyOrders() {
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [range, setRange] = useState("all");
  const [sort, setSort] = useState("newest");
  const [subOnly, setSubOnly] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/auth?redirect=/account/orders"); return; }
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, [user, loading, nav]);

  const filtered = useMemo(() => {
    let list = [...orders];
    if (status !== "all") list = list.filter((o) => o.status === status);
    if (subOnly) list = list.filter((o) => o.is_subscription);
    if (range !== "all") {
      const days = parseInt(range, 10);
      const cutoff = Date.now() - days * 24 * 3600 * 1000;
      list = list.filter((o) => new Date(o.created_at).getTime() >= cutoff);
    }
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter((o) =>
        o.order_number.toLowerCase().includes(needle) ||
        (o.items || []).some((it) => (it.name || "").toLowerCase().includes(needle)) ||
        (o.coupon_code || "").toLowerCase().includes(needle)
      );
    }
    list.sort((a, b) => {
      if (sort === "oldest") return new Date(a.created_at) - new Date(b.created_at);
      if (sort === "amount_desc") return b.total - a.total;
      if (sort === "amount_asc") return a.total - b.total;
      return new Date(b.created_at) - new Date(a.created_at);
    });
    return list;
  }, [orders, q, status, range, sort, subOnly]);

  const clearAll = () => { setQ(""); setStatus("all"); setRange("all"); setSort("newest"); setSubOnly(false); };
  const hasFilters = q || status !== "all" || range !== "all" || sort !== "newest" || subOnly;

  if (loading || !user) {
    return <div className="max-w-[1400px] mx-auto py-32 text-center text-ink-500">Loading…</div>;
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12 lg:py-16">
      <Link to="/account" className="inline-flex items-center gap-1 text-xs text-ink-500 hover:text-ink-900" data-testid="my-orders-back">
        <ChevronLeft size={14} /> Back to Account
      </Link>
      <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Account</div>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl text-ink-900">My Orders</h1>
          <div className="mt-2 text-sm text-ink-500">{orders.length} total · {filtered.length} shown</div>
        </div>
        <Link to="/shop" className="btn-gold-outline px-5 py-2 text-xs uppercase tracking-widest" data-testid="my-orders-shop-cta">
          Continue shopping
        </Link>
      </div>

      {/* Filters bar */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-4 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by order #, product name, or coupon"
            className="rounded-none pl-9 h-11"
            data-testid="my-orders-search"
          />
        </div>
        <div className="md:col-span-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-none h-11" data-testid="my-orders-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {STATUS_OPTS.map((s) => (
                <SelectItem key={s} value={s} data-testid={`filter-status-${s}`}>
                  {s === "all" ? "All statuses" : STATUS_LABEL[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="rounded-none h-11" data-testid="my-orders-range-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {RANGE_OPTS.map((r) => (
                <SelectItem key={r.v} value={r.v} data-testid={`filter-range-${r.v}`}>{r.l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="rounded-none h-11" data-testid="my-orders-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {SORT_OPTS.map((r) => (
                <SelectItem key={r.v} value={r.v} data-testid={`sort-${r.v}`}>{r.l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2 flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="my-orders-subonly">
            <input type="checkbox" checked={subOnly} onChange={(e) => setSubOnly(e.target.checked)} className="accent-black" />
            Subscriptions only
          </label>
        </div>
      </div>

      {hasFilters && (
        <div className="mt-3">
          <button onClick={clearAll} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-ink-500 hover:text-ink-900" data-testid="my-orders-clear">
            <X size={12} /> Clear filters
          </button>
        </div>
      )}

      {/* List */}
      <div className="mt-8 space-y-4">
        {filtered.length === 0 && (
          <div className="border hairline p-12 bg-cream-50 text-center" data-testid="my-orders-empty">
            <Package size={28} className="mx-auto text-ink-300" />
            <div className="mt-3 text-sm text-ink-700">
              {orders.length === 0 ? "You haven't placed any orders yet." : "No orders match your filters."}
            </div>
            {orders.length === 0 && (
              <Link to="/shop" className="mt-4 inline-flex btn-primary-ink px-5 py-2 text-xs uppercase tracking-widest">
                Shop the collection
              </Link>
            )}
          </div>
        )}

        {filtered.map((o) => (
          <div key={o.id} className="border hairline p-5 sm:p-6 bg-white" data-testid={`myorder-${o.order_number}`}>
            <div className="flex flex-wrap justify-between gap-4">
              <div>
                <div className="eyebrow">Order</div>
                <div className="mt-1 font-mono text-sm tracking-wider">#{o.order_number}</div>
                <div className="mt-1 text-xs text-ink-500">
                  Placed {new Date(o.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                </div>
                {o.is_subscription && (
                  <div className="mt-1 inline-block text-[10px] uppercase tracking-widest bg-gold-500/10 text-gold-600 px-2 py-0.5">
                    Subscription · {o.subscription_cadence}
                  </div>
                )}
              </div>
              <div>
                <div className="eyebrow">Status</div>
                <div className="mt-1 text-sm">{STATUS_LABEL[o.status] || o.status}</div>
                {o.eta && <div className="text-xs text-ink-500 mt-1">ETA {o.eta}</div>}
              </div>
              <div>
                <div className="eyebrow">Payment</div>
                <div className="mt-1 text-sm">{(o.payment_method || "").toUpperCase()}</div>
                <div className="text-xs text-ink-500 mt-1">{o.payment_status}</div>
              </div>
              <div>
                <div className="eyebrow">Total</div>
                <div className="mt-1 font-display text-xl">₹{o.total.toFixed(0)}</div>
                <div className="text-[11px] text-ink-500 mt-0.5">incl. GST</div>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {(o.items || []).map((it, i) => (
                <div key={i} className="flex items-center gap-2 border hairline px-2 py-1 bg-cream-50">
                  <img src={it.image} alt="" className="w-8 h-10 object-cover bg-cream-200" />
                  <div className="text-[11px] leading-tight">
                    <div className="text-ink-900 line-clamp-1 max-w-[160px]">{it.name}</div>
                    <div className="text-ink-500">× {it.quantity}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 flex flex-wrap gap-3 items-center">
              <Link
                to={`/track?n=${o.order_number}`}
                className="btn-gold-outline px-4 py-2 text-[11px] uppercase tracking-widest inline-flex items-center gap-2"
                data-testid={`track-${o.order_number}`}
              >
                Track order <ArrowRight size={12} />
              </Link>
              <a
                href={invoiceHref(o.order_number)}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary-ink px-4 py-2 text-[11px] uppercase tracking-widest inline-flex items-center gap-2"
                data-testid={`invoice-${o.order_number}`}
              >
                <FileDown size={12} /> Download invoice
              </a>
              <span className="text-[11px] text-ink-500 ml-auto flex items-center gap-1">
                <Filter size={10} /> Non-returnable · GST included
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
